import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { readLinkToken } from "@/lib/linkToken";
import { canSeeAdminCenter } from "@/lib/roles";
import MfaChallenge from "@/components/MfaChallenge";
import { MfaEnroll } from "@/components/MfaEnroll";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaMode, setMfaMode] = useState(null); // null | "challenge" | "setup"

  const finish = (data) => {
    setUser(data);
    toast.success("Welcome back!");
    if (readLinkToken("/accept-invite")) return navigate("/accept-invite");
    navigate(canSeeAdminCenter(data.role) ? "/admin" : "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    toast.dismiss(); // clear any stale error toast from a prior attempt so success isn't shown alongside it
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) { setMfaMode("challenge"); return; }
      if (data.mfa_enrollment_required) { setMfaMode("setup"); return; }
      finish(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (mfaMode === "challenge") {
    return (
      <AuthShell title="Verify it's you" subtitle="One more step to keep your account secure.">
        <MfaChallenge onSuccess={finish} onCancel={() => setMfaMode(null)} />
      </AuthShell>
    );
  }

  if (mfaMode === "setup") {
    return (
      <AuthShell title="Two-factor setup required" subtitle="Your role requires two-factor authentication before you can continue.">
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="mfa-forced-setup-notice">
          <ShieldCheck size={16} /> Set up an authenticator app now to finish signing in.
        </div>
        <MfaEnroll onDone={(res) => (res?.user ? finish(res.user) : navigate("/login"))} onCancel={() => setMfaMode(null)} />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your DEI TradeMatch AI account."
      footer={<>Don't have an account? <Link to="/register" className="text-[#2563EB] font-medium" data-testid="link-register">Create one</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
            placeholder="you@company.com"
          />
        </div>
        <div>
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-slate-700">Password</label>
            <Link to="/forgot-password" className="text-xs text-[#2563EB]" data-testid="link-forgot-password">Forgot password?</Link>
          </div>
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="mt-1.5 w-full h-11 px-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] focus:border-[#2563EB] outline-none"
            placeholder="••••••••"
          />
        </div>
        <button
          type="submit" disabled={loading}
          data-testid="login-submit-button"
          className="w-full h-11 rounded-md bg-[#2563EB] text-white font-medium hover:bg-[#1D4ED8] transition-colors flex items-center justify-center disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px bg-slate-200 flex-1" /> OR <div className="h-px bg-slate-200 flex-1" />
      </div>
      <GoogleButton />
    </AuthShell>
  );
}
